import { useMutation } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { useInteraction } from "./useInteraction";
import { getSocketInstance } from "./useScreenSocket";

interface AudioContextType {
    onPlay: () => void;
    onPause: () => void;
    onEnable: () => void;
    onDisable: () => void;
    musicEnabled: boolean
    onVolumeChange: (volume: number) => void;
    onTrackChange: () => void;
    isPlaying: boolean;
    isPending: boolean;
    volume: number;
    onSyncEnabled: () => void;
    onSyncDisabled: () => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

export type AudioProviderProps = React.PropsWithChildren<{
    url: string,
    onError: () => Promise<void>
}>

export function AudioProvider({ children, url, onError: onUnrecoverableError }: AudioProviderProps) {
    const { deviceId: _devId, getScreenInfo, screenInfo } = useAuth()
    const deviceId = _devId ?? ""
    const { hasInteracted } = useInteraction()
    const [isPlaying, setIsPlaying] = useState(hasInteracted && !!screenInfo?.music_playing)
    const [volume, setVolume] = useState<number>(1)
    // Mirror of `volume` readable synchronously from async callbacks (createAudio.onSuccess).
    const volumeRef = useRef(volume)
    volumeRef.current = volume
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [musicEnabled, setMusicEnabled] = useState(!!screenInfo?.music_integration_enabled)
    const MAX_RETRIES = 5
    const INITIAL_RETRY_DELAY_MS = 1000
    type VideoPlayerState = {
        state: 'playing' | 'started' | 'paused' | 'ended' | 'error'
    }
    const videoPlayerStateRef = useRef<Record<string, VideoPlayerState>>({})

    // Apply volume/mute to the live audio element when volume changes. The element is
    // created asynchronously, so the initial value is applied in createAudio.onSuccess
    // (via volumeRef). This replaces the old `while (!audioRef.current)` busy-wait that
    // logged "waiting for 200ms" forever whenever the audio failed to initialize.
    useEffect(() => {
        if (!audioRef.current) return
        audioRef.current.volume = volume
        audioRef.current.muted = volume === 0
    }, [volume])

    useEffect(() => {
        if (musicEnabled) return
        try {
            window.onmessage = (event) => {
                if (typeof event.data?.event !== 'string') return
                const id = event.data.data?.id
                function collect(data: VideoPlayerState) {
                    const elem = document.getElementById(id) as HTMLAudioElement
                    if (elem.muted || elem.volume === 0) return
                    videoPlayerStateRef.current[id] = data
                    const isAnyPlaying = Object.values(videoPlayerStateRef.current).some((v) => v.state === 'playing')
                    if (isAnyPlaying) {
                        return onVolumeChange(0)
                    }
                    onVolumeChange(1)
                }
                switch (event.data.event) {
                    case 'video-play':
                        collect({ state: 'playing' })
                        break
                    case 'video-start':
                        collect({ state: 'started' })
                        break
                    case 'video-pause':
                        collect({ state: 'paused' })
                        break
                    case 'video-end':
                        collect({ state: 'ended' })
                        break
                    case 'video-error':
                        collect({ state: 'error' })
                        break
                }
            }
        } catch (e) { console.error(e) }
    }, [musicEnabled])

    function emitMusicEvent(event: string, rest?: Record<string, any>) {
        const socket = getSocketInstance()
        socket?.emit("broadcastMessage", {
            roomId: deviceId,
            messageContent: event,
            messageData: {
                from: "player",
                ...rest,
            },
        })
    }

    // Detach handlers and release a specific <audio>. Used both for the live element
    // (disposeAudio) and for transient elements discarded between load retries.
    function cleanupAudio(audio: HTMLAudioElement | null) {
        if (!audio) return
        audio.pause()
        audio.oncanplay = null
        audio.onloadeddata = null
        audio.onerror = null
        audio.onplay = null
        audio.onpause = null
        audio.src = ''
        audio.load()
        audio.remove()
    }

    // Create and buffer an <audio> for the stream, retrying transient load failures
    // with exponential backoff. Resolves once the element can begin playing; rejects
    // with a descriptive error once retries are exhausted (e.g. the CustomChannels
    // zone/location was removed and the stream now returns 401). This is a *load*
    // failure and is intentionally distinct from an autoplay block (NotAllowedError),
    // which can only originate from play() — see onPlay below.
    function loadAudioElement(attempt = 0): Promise<HTMLAudioElement> {
        const audio = new Audio(url)
        audio.crossOrigin = "anonymous"
        audio.load()
        return new Promise<HTMLAudioElement>((resolve, reject) => {
            let settled = false
            const onReady = () => {
                if (settled) return
                settled = true
                resolve(audio)
            }
            // `canplaythrough` (HAVE_ENOUGH_DATA) is unreliable for continuous
            // internet-radio streams that haven't started playing yet: the browser
            // won't buffer far enough ahead to ever fire it. `canplay`/`loadeddata`
            // fire as soon as playback can begin and are reliable for streams.
            audio.oncanplay = onReady
            audio.onloadeddata = onReady
            if (audio.readyState >= audio.HAVE_CURRENT_DATA) onReady()
            audio.onerror = () => {
                if (settled) return
                settled = true
                const mediaError = audio.error
                const detail = `code ${mediaError?.code ?? '?'}${mediaError?.message ? `: ${mediaError.message}` : ''}`
                cleanupAudio(audio)
                if (attempt < MAX_RETRIES) {
                    const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt)
                    console.warn(`Music stream failed to load (${detail}); retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`)
                    setTimeout(() => loadAudioElement(attempt + 1).then(resolve, reject), delay)
                } else {
                    reject(new Error(`Music stream failed to load after ${MAX_RETRIES + 1} attempts (${detail})`))
                }
            }
        })
    }

    const { mutateAsync: createAudio } = useMutation({
        mutationFn: () => loadAudioElement(),
        onSuccess(audio) {
            audioRef.current = audio
            audio.volume = volumeRef.current
            audio.muted = volumeRef.current === 0
            audio.onplay = () => {
                setIsPlaying(true)
                if (screenInfo?.group_id) emitMusicEvent("groupMusicResumed", { groupId: screenInfo.group_id })
                else emitMusicEvent("musicResumed")
            }
            audio.onpause = () => {
                setIsPlaying(false)
                if (screenInfo?.group_id) emitMusicEvent("groupMusicPaused", { groupId: screenInfo.group_id })
                else emitMusicEvent("musicPaused")
            }
        },
        async onError(err) {
            // Reaching here means the stream could not be loaded after retries — a real
            // load error, not an autoplay block. Surface the actual reason to the admin...
            emitMusicEvent("musicError", {
                error: err instanceof Error ? err.message : "Music stream failed to load",
                groupId: screenInfo?.group_id,
            })
            // ...tell the server the stream is unreachable so it can reconcile
            // SignageStudio state with CustomChannels (the zone/location is gone)...
            emitMusicEvent("musicStreamUnavailable", {
                zoneId: screenInfo?.zone_id,
                groupId: screenInfo?.group_id,
            })
            // ...then ask the app to drop the (likely stale) listener.
            try { await onUnrecoverableError() } catch { /* best-effort recovery */ }
        }
    })

    function disposeAudio() {
        cleanupAudio(audioRef.current)
        audioRef.current = null
    }

    const { mutateAsync: onTrackChange, isPending: isTrackChanging } = useMutation({
        async mutationFn() {
            disposeAudio()
            // Errors are surfaced/handled by createAudio.onError; swallow the rejection
            // here so it doesn't become an uncaught promise rejection.
            createAudio().catch(() => { })
        },
    })

    const { mutateAsync: onPlay, isPending: isPlayLoading } = useMutation({
        async mutationFn() {
            audioRef.current ??= await createAudio()
            await audioRef.current?.play()
        },
        onError(err) {
            const isNotAllowedError = err instanceof DOMException && err.name === 'NotAllowedError'

            if (isNotAllowedError) {
                emitMusicEvent("musicError", { error: "Blocked by browser. Please interact with the player screen to play audio", groupId: screenInfo?.group_id })
            }
        },
        onSuccess() {
        }
    })

    // preload
    useEffect(() => {
        if (!musicEnabled) return
        // Errors are surfaced/handled by createAudio.onError; swallow the rejection here
        // so it doesn't become an uncaught promise rejection.
        createAudio().catch(() => { })

        return () => {
            disposeAudio()
        }
    }, [url, musicEnabled])

    // Graceful Joining for New/Reconnecting Devices
    // useEffect(() => {
    //     if (screenInfo?.music_playing) {
    //         onPlay();
    //     } else {
    //         onPause();
    //     }
    // }, [screenInfo?.music_playing, url, hasInteracted]);

    function onPause() {
        audioRef.current?.pause()
    };
    function onVolumeChange(volume: number) {
        if (volume < 0 || volume > 1) throw new Error("Volume must be between 0 and 1")

        setVolume(volume)
    };
    function onEnable() {
        getScreenInfo(true)
        setMusicEnabled(true)
        onPlay()
    };
    function onDisable() {
        setMusicEnabled(false)
        onPause()
    }

    function onSyncEnabled() {
        getScreenInfo(true).then((screen) => {
            if (screen?.music_playing) {
                onPlay()
            } else {
                onPause()
            }
        })
    }
    function onSyncDisabled() {
        getScreenInfo(true).then((screen) => {
            if (screen?.music_playing) {
                onPlay()
            } else {
                onPause()
            }
        })
    }

    const isPending = isPlayLoading || isTrackChanging

    return (
        <AudioContext.Provider value={{ onDisable, onEnable, musicEnabled, onPlay, onPause, onVolumeChange, onTrackChange, isPlaying, isPending, volume, onSyncEnabled, onSyncDisabled }}>
            {children}
        </AudioContext.Provider>
    );
}

export function useAudio() {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error("useAudio must be used within an AudioProvider");
    }
    return context;
}